'use client';

import { useRouter } from 'next/navigation';
import {
  createContext,
  type Dispatch,
  type SetStateAction,
  startTransition,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { revalidateCurrentUser } from '@/features/users/server/actions';
import type { AccountProfile } from '@/features/users/types';

type UserContextValue = {
  user: AccountProfile;

  setUser: Dispatch<SetStateAction<AccountProfile>>;

  refreshUser: () => Promise<void>;

  refreshing: boolean;
};

const UserContext = createContext<UserContextValue | undefined>(undefined);

export function UserProvider({
  initialUser,
  children,
}: {
  initialUser: AccountProfile;
  children: React.ReactNode;
}) {
  const [user, setUser] = useState<AccountProfile>(initialUser);
  const [refreshing, setRefreshing] = useState(false);
  const router = useRouter();

  // Keep local state in sync when server provides a new user after router.refresh()
  useEffect(() => {
    startTransition(() => {
      setUser(initialUser);
    });
  }, [initialUser]);

  const refreshUser = useCallback(async () => {
    setRefreshing(true);
    try {
      await revalidateCurrentUser();
      router.refresh();
    } finally {
      setRefreshing(false);
    }
  }, [router]);

  const value = useMemo(
    () => ({ user, setUser, refreshUser, refreshing }),
    [user, refreshUser, refreshing],
  );

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
}

export function useUser() {
  const ctx = useContext(UserContext);
  if (ctx === undefined) throw new Error('useUser must be used within a UserProvider');
  return ctx;
}

/**
 * Hook to safely check if user is authenticated without throwing an error.
 * Returns true if user context is available (user is logged in), false otherwise.
 * Use this in components that may be rendered in both public and protected routes.
 */
export function useIsAuthenticated(): boolean {
  const ctx = useContext(UserContext);
  return ctx !== undefined;
}
